import { AtlasUserOverride } from 'api/atlasClient'
import { CerebroPaginatedResponse } from 'api/Cerebro/types'
import { Expression } from 'api/hulkApi'

import {
    emptySegmentEnvelope,
    querySegmentData,
} from './marketAnalysisSegmentsQuery'
import {
    buildAsinSearchPredicate,
    buildBrandSearchPredicate,
} from './predicates'

export interface MarketAnalysisSegmentAsinRow {
    asin: string
}

export interface MarketAnalysisSegmentBrandRow {
    brand_stable: string
}

export interface MarketAnalysisSegmentSellerRow {
    seller_id: string
    seller_name: string
}

export function searchMarketAnalysisSegmentAsins(
    segmentIds: string[],
    query: string
): Promise<CerebroPaginatedResponse<MarketAnalysisSegmentAsinRow>> {
    return querySegmentData<MarketAnalysisSegmentAsinRow>({
        segmentIds,
        dimensions: ['asin'],
        predicate: buildAsinSearchPredicate(query),
    })
}

export function searchMarketAnalysisSegmentBrands(
    segmentIds: string[],
    query: string
): Promise<CerebroPaginatedResponse<MarketAnalysisSegmentBrandRow>> {
    // Project `brand_stable` so selected values line up with the widget filter.
    return querySegmentData<MarketAnalysisSegmentBrandRow>({
        segmentIds,
        dimensions: ['brand_stable'],
        predicate: buildBrandSearchPredicate(query),
    })
}

export function searchMarketAnalysisSegmentSellers(
    segmentIds: string[],
    query: string
): Promise<CerebroPaginatedResponse<MarketAnalysisSegmentSellerRow>> {
    // Match against the text-optimized `seller_name_searchable` column.
    // Project both `seller_id` (used by the widget query filter) and
    // `seller_name` (used for display in the typeahead/chip). Drop rows
    // with no seller_name so the typeahead doesn't surface unnamed sellers.
    const hasName: Expression = {
        _and: [
            { field: 'seller_name', operator: '_not_null', value: true },
            { field: 'seller_name', operator: '_neq', value: '' },
        ],
    }
    const predicate: Expression = query
        ? {
              _and: [
                  hasName,
                  {
                      field: 'seller_name_searchable',
                      operator: '_contains',
                      value: query,
                  },
              ],
          }
        : hasName
    return querySegmentData<MarketAnalysisSegmentSellerRow>({
        segmentIds,
        dimensions: ['seller_id', 'seller_name'],
        predicate,
    })
}

// Resolve seller names for a set of seller IDs already persisted in the
// filter state — the typeahead's prefetch/search may not include them, so
// the filter chip would otherwise show the raw IDs.
export function resolveMarketAnalysisSegmentSellersByIds(
    segmentIds: string[],
    sellerIds: string[],
    override?: AtlasUserOverride
): Promise<CerebroPaginatedResponse<MarketAnalysisSegmentSellerRow>> {
    if (sellerIds.length === 0) {
        return Promise.resolve(
            emptySegmentEnvelope<MarketAnalysisSegmentSellerRow>()
        )
    }
    return querySegmentData<MarketAnalysisSegmentSellerRow>({
        segmentIds,
        dimensions: ['seller_id', 'seller_name'],
        predicate: { field: 'seller_id', operator: '_in', value: sellerIds },
        override,
    })
}
